import { CheckCircle2, AlertTriangle, XCircle, Info } from "lucide-react";

/**
 * Brief 3.5 PR 2 §1.2 + PR 3 §3.1 — Recommendation banner.
 *
 * Reads its verdict from the server-computed `verdict` payload
 * (`services/verdict.py` is the single source of truth for the
 * tone + primary_blocker decision). The banner is now a thin
 * presentation layer over that decision — no rules live here.
 *
 * Copy table (Brief 3.5 §1.2, preserved verbatim):
 *   • tone=approve     → "Recommend: approve. All documents verified, no open findings."
 *   • tone=conditional → "Recommend: approve once {missing_doc} is received."
 *                        (suppressed if no required-but-unuploaded slot found in
 *                         the documents prop — falls through to a generic line.)
 *   • tone=review      → "Recommend: review {N} flagged items, then approve."
 *   • tone=blocker     → "Recommend: do not approve until {primary_blocker} is resolved."
 *
 * Suppressed for non-actionable lifecycle states (terminal /
 * pre-decision) — matches Dioni's signoff that pre-decision merchants
 * shouldn't see a recommendation yet.
 */
const ACTIONABLE_STATES = new Set([
  "submitted", "under_review", "info_requested",
]);

function _firstMissingDocName(documents) {
  // documents is the unified.documents shape: array of slots, each with
  // `name`, `uploaded` (bool), `required` (bool). Returns the first
  // required-but-not-uploaded slot label.
  const missing = (documents || []).filter(
    (d) => d.required && !d.uploaded,
  );
  if (!missing.length) return null;
  return missing[0].label || missing[0].slot || missing[0].name || "outstanding document";
}

const TONE_STYLES = {
  blocker:     { icon: XCircle,        wrap: "border-rose-400/30 bg-rose-500/[0.05]",       headline: "text-rose-100",     details: "text-rose-200/80" },
  conditional: { icon: Info,           wrap: "border-amber-400/30 bg-amber-500/[0.05]",     headline: "text-amber-100",    details: "text-amber-200/80" },
  review:      { icon: AlertTriangle,  wrap: "border-amber-400/30 bg-amber-500/[0.05]",     headline: "text-amber-100",    details: "text-amber-200/80" },
  approve:     { icon: CheckCircle2,   wrap: "border-emerald-400/30 bg-emerald-500/[0.05]", headline: "text-emerald-100",  details: "text-emerald-200/80" },
};

function _composeFromVerdict({ verdict, documents }) {
  // verdict can be null while loading; render nothing in that case.
  if (!verdict) return null;
  const tone = verdict.tone;
  const sev = verdict.severity_counts || {};
  const reviews = (sev.suspicious || 0) + (sev.minor || 0)
    + (verdict.counts?.review || 0);
  const verifiedCount = verdict.counts?.verified || 0;

  if (tone === "blocker") {
    const primary = verdict.primary_blocker || "critical finding";
    const screening = verdict.screening_counts || {};
    return {
      tone,
      action: `Recommend: do not approve until ${primary} is resolved.`,
      details: [
        `${verifiedCount} verified`,
        (sev.critical || 0) > 0 ? `${sev.critical} blocks approval` : null,
        (screening.confirmed_match || 0) > 0
          ? `${screening.confirmed_match} sanctions match` : null,
        reviews > 0 ? `${reviews} to review` : null,
      ].filter(Boolean).join(" · "),
    };
  }

  if (tone === "conditional") {
    const missingName = _firstMissingDocName(documents);
    const action = missingName
      ? `Recommend: approve once ${missingName} is received.`
      : "Recommend: approve once outstanding documents are received.";
    const missingCount = (documents || []).filter(
      (d) => d.required && !d.uploaded,
    ).length;
    return {
      tone,
      action,
      details: [
        `${verifiedCount} verified`,
        missingCount > 0 ? `${missingCount} missing` : null,
        reviews > 0 ? `${reviews} to review` : null,
      ].filter(Boolean).join(" · "),
    };
  }

  if (tone === "review") {
    return {
      tone,
      action: `Recommend: review ${reviews} flagged item${reviews === 1 ? "" : "s"}, then approve.`,
      details: `${verifiedCount} verified · ${reviews} to review`,
    };
  }

  // tone === "approve"
  return {
    tone,
    action: "Recommend: approve.",
    details: "All documents verified, no open findings.",
  };
}

export default function RecommendationBanner({ identity, verdict, documents }) {
  if (!ACTIONABLE_STATES.has(identity?.status)) return null;
  const composed = _composeFromVerdict({ verdict, documents });
  if (!composed) return null;

  const style = TONE_STYLES[composed.tone] || TONE_STYLES.review;
  const Icon = style.icon;

  return (
    <div
      data-testid="merchant-recommendation-banner"
      data-tone={composed.tone}
      className={`rounded-2xl border ${style.wrap} px-5 py-4 flex items-start gap-3`}
    >
      <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${style.headline}`} />
      <div className="min-w-0 flex-1">
        <div className={`text-[14px] font-medium ${style.headline}`} data-testid="merchant-recommendation-action">
          {composed.action}
        </div>
        {composed.details && (
          <div className={`mt-0.5 text-[12px] ${style.details}`} data-testid="merchant-recommendation-details">
            {composed.details}
          </div>
        )}
      </div>
    </div>
  );
}

// Exported for unit testing the presentation in isolation if needed.
export { _composeFromVerdict };
